import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectUpdateMyDocumentsListParentFolderId,
  selectUpdateMyDocumentsListSubjectId,
  setUpdateMyDocumentsList,
} from 'reducers/misc';
import { PopupOrPanel } from '../../components/popupsAndPanels/PopupAndPanels';
import { Spinner } from 'components/spinner/Spinner';
import FileUploadPanel from '../../helpers//FileUploadPanel/FileUploadPanel';
import { Formik } from 'formik';
import FormTextField from 'components/formComponents/FormTextField';
import * as Yup from 'yup';
import { MyDocumentsApi } from '../../api/myDocumentsApi';

type Props = {
  panelOpen: boolean;
  togglePanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFileId?: number;
};

export const UploadDocuments: React.FC<Props> = ({
  panelOpen,
  togglePanelOpen,
  selectedFileId,
}) => {
  const updateListSubjectId: any = useSelector(
    selectUpdateMyDocumentsListSubjectId
  );

  const parentFoldersId = useSelector(
    selectUpdateMyDocumentsListParentFolderId
  );

  const dispatch = useDispatch();

  const [error, setError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<any>(null);
  const [uploadedFileURL, setUploadedFileURL] = useState<any>(null);
  const [existingFileChanged, setExistingFileChanged] =
    useState<boolean>(false);
  const [fileNameError, setFileNameError] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');

  useEffect(() => {
    if (selectedFileId) {
      loadExistingDocument();
    }
  }, []);

  const initialValues: any = {
    fileName: '',
  };

  const validationSchemaDocUpload = Yup.object({});

  const loadExistingDocument = async () => {
    setIsSubmitting(true);

    const result =
      selectedFileId && (await MyDocumentsApi.getFile(selectedFileId));

    if (result.data) {
      initialValues.fileName = result.data.documentName;
      setUploadedFile(
        new File(
          [result.data.documentUrl],
          result.data.documentNameAndExtension
        )
      );
      setUploadedFileURL(result.data.documentUrl);
      setIsSubmitting(false);
    }

    if (result.error) {
      setError(error);
      setIsSubmitting(false);
    }
  };

  const submitFileForm = async (values: any) => {
    let noErrs: boolean = true;
    setFileNameError('');
    setFileError('');
    setError('');

    if (!values.fileName) {
      noErrs = false;
      setFileNameError('A file name is required');
    }

    if (!uploadedFile && !uploadedFileURL) {
      noErrs = false;
      setFileError('A file is required');
    }

    if (noErrs) {
      setIsSubmitting(true);

      let result = null;

      if (selectedFileId) {
        const formData = new FormData();
        formData.append(
          'DocumentsId',
          selectedFileId ? selectedFileId.toString() : ''
        );
        formData.append('Document', existingFileChanged ? uploadedFile : null);
        formData.append('DocumentName', values.fileName);
        formData.append('bFileUpdated', existingFileChanged.toString());

        result = await MyDocumentsApi.updateFile(formData);
      } else {
        const formData = new FormData();
        formData.append('SubjectsId', updateListSubjectId?.toString());
        if (parentFoldersId !== null) {
          formData.append('ParentFoldersId', `${parentFoldersId}`);
        }
        formData.append('Document', uploadedFile);
        formData.append('DocumentName', values.fileName);

        result = await MyDocumentsApi.createFile(formData);
      }

      if (result.data && result.data.status === '1') {
        dispatch(setUpdateMyDocumentsList(true));
        togglePanelOpen(false);
      }

      if (result.error) {
        let errorMsgCode = 
          result.error[0].messageCode.indexOf('File_Too_Large') > -1 ? 
          "File exceeds 100MB size limit" :
          result.error[0].messageCode;

        setError(errorMsgCode);
        setIsSubmitting(false);
      }
    }
  };

  return (
    <PopupOrPanel
      isPopup={true}
      isOpen={panelOpen}
      hdrTitle={'Upload file'}
      position="right"
      setTop={true}
      noBackground={true}
      noHeader={true}
      noContentContainer={true}
      givenClass="DocumentHandlePage"
      noFooter={true}
      handleClose={() => togglePanelOpen(false)}
      handleSecondaryBtn={() => togglePanelOpen(false)}
      width={560}
      isSubmitting={isSubmitting}
      noOverflowToggle={true}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={submitFileForm}
        validationSchema={validationSchemaDocUpload}
        validateOnBlur={false}
      >
        {({ submitForm, isSubmitting, values }) => (
          <div className="WidthContent">
            <span className="BtnBack" onClick={() => togglePanelOpen(false)}>
              <span className="BB_Icon"></span>
              <span className="BB_Txt">Back</span>
            </span>

            <div className="DHPageContent">
              <h2>{selectedFileId ? 'Edit' : 'Upload'} file</h2>

              {error && <p className="GeneralError2">{error}</p>}

              <div
                className={
                  'DocDetailsRow' + (isSubmitting ? ' DisableDocForm' : '')
                }
              >
                <div className="DocDRInner">
                  <div
                    className={'DocRowBlock' + (fileError ? ' ErrorBlock' : '')}
                  >
                    <div className="DocFileUpload">
                      <FileUploadPanel
                        label="File"
                        field="uploadedFile"
                        dropzoneText=""
                        onFileUploadEnd={(file) => {
                          setUploadedFile(file);

                          if (selectedFileId) {
                            setExistingFileChanged(true);
                          }
                        }}
                        file={uploadedFile}
                        uploadedImageUrl={uploadedFileURL}
                        onlyShowFileName={true}
                        maximumFileSizeInMB={100}
                        alternativeErrMsg={true}
                        fileInstructions={"100MB file size limit"}
                      />
                      
                      {fileError && (
                        <div className="ErrorLabel">{fileError}</div>
                      )}
                    </div>
                    {uploadedFile && (
                      <div className="RemoveFileRow">
                        <div
                          className="Btn BtnOrange"
                          onClick={() => {
                            setUploadedFile(null);
                            setUploadedFileURL(null);
                          }}
                        >
                          Remove File
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className={
                      'DocRowBlock' + (fileNameError ? ' ErrorBlock' : '')
                    }
                  >
                    <FormTextField
                      label="File name"
                      field="fileName"
                      required={true}
                      labelPosition="left"
                      value={values.fileName}
                      placeholder="File name"
                    />
                    {fileNameError && (
                      <div className="ErrorLabel">{fileNameError}</div>
                    )}
                  </div>
                </div>
              </div>

              <div
                className="Btn BtnOrange DocFormSubmitBtn"
                onClick={() => submitForm()}
              >
                Save
              </div>
            </div>
          </div>
        )}
      </Formik>
      {isSubmitting && <Spinner />}
    </PopupOrPanel>
  );
};
